import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, User, LogIn } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { CartIcon } from "@/components/cart/CartIcon";
import { ProductSearch } from "@/components/search/ProductSearch";
import logo from "@/assets/logo.jpg";

// iOS detection
const isIOS = typeof navigator !== 'undefined' && 
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || 
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

function getIsPortrait() {
  if (typeof window === "undefined") return true;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(orientation: portrait)").matches;
  }
  return window.innerHeight >= window.innerWidth;
}

const navLinks = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Products", href: "/products" },
  { name: "Blog", href: "/blog" },
  { name: "Gift Cards", href: "/gift-cards" },
  { name: "Track Order", href: "/track-order" },
  { name: "Testimonials", href: "/testimonials" },
  { name: "Contact", href: "/contact" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPortrait, setIsPortrait] = useState(getIsPortrait());
  const location = useLocation();
  const { user, isAdmin } = useAuth();

  // Use static menu for ALL iOS portrait pages (not just /products)
  // This prevents Framer Motion from causing compositor glitches during navigation
  const useStaticMenu = isIOS && isPortrait;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Track orientation changes for iOS portrait detection
  useEffect(() => {
    const updatePortrait = () => setIsPortrait(getIsPortrait());
    window.addEventListener("resize", updatePortrait);
    window.addEventListener("orientationchange", updatePortrait);
    return () => {
      window.removeEventListener("resize", updatePortrait);
      window.removeEventListener("orientationchange", updatePortrait);
    };
  }, []);

  return (
    <header
      className={`left-0 right-0 z-50 transition-all duration-300 ease-in-out ${
        isScrolled
          ? "fixed top-0 bg-card/95 md:bg-card/70 md:backdrop-blur-xl shadow-[0_2px_20px_rgba(0,0,0,0.08)] py-3"
          : "relative bg-card py-5"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logo}
              alt="Nurture Skin Studio"
              width={48}
              height={48}
              className="h-12 w-12 rounded-full shadow-soft object-cover"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-300 animated-underline whitespace-nowrap ${
                  location.pathname === link.href
                    ? "text-primary"
                    : isScrolled
                    ? "text-foreground hover:text-primary"
                    : "text-foreground/80 hover:text-primary"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Search, Cart, Account and CTA Button */}
          <div className="hidden xl:flex items-center gap-3">
            <ProductSearch />
            <CartIcon />
            {!user ? (
              <Button variant="ghost" size="icon" asChild>
                <Link to="/admin/login">
                  <LogIn className="h-5 w-5" />
                </Link>
              </Button>
            ) : isAdmin ? (
              <Button variant="ghost" size="icon" asChild>
                <Link to="/admin">
                  <User className="h-5 w-5" />
                </Link>
              </Button>
            ) : (
              <Button variant="ghost" size="icon">
                <User className="h-5 w-5" />
              </Button>
            )}
            <Button
              variant="whatsapp"
              size="default"
              asChild
            >
              <a
                href="https://api.whatsapp.com/send?phone=2347065732481"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                Shop on WhatsApp
              </a>
            </Button>
          </div>

          {/* Mobile: Search, Cart, and Menu Button */}
          <div className="flex items-center gap-2 xl:hidden">
            <ProductSearch />
            <CartIcon />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu - STATIC version for ALL iOS portrait (no Framer Motion) */}
      {isMobileMenuOpen && useStaticMenu && (
        <div className="xl:hidden bg-card/98 border-t border-border">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <div key={link.name}>
                <Link
                  to={link.href}
                  className={`block py-2 text-base font-medium transition-colors ${
                    location.pathname === link.href
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  }`}
                >
                  {link.name}
                </Link>
              </div>
            ))}
            <div className="pt-4 space-y-3">
              {!user ? (
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  asChild
                >
                  <Link to="/admin/login" className="flex items-center justify-center gap-2">
                    <LogIn className="h-5 w-5" />
                    Login
                  </Link>
                </Button>
              ) : isAdmin ? (
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  asChild
                >
                  <Link to="/admin" className="flex items-center justify-center gap-2">
                    <User className="h-5 w-5" />
                    Admin Dashboard
                  </Link>
                </Button>
              ) : null}
              <Button
                variant="whatsapp"
                size="lg"
                className="w-full"
                asChild
              >
                <a
                  href="https://api.whatsapp.com/send?phone=2347065732481"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <MessageCircle className="h-5 w-5" />
                  Shop on WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu - ANIMATED version for non-iOS or landscape */}
      {isMobileMenuOpen && !useStaticMenu && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="xl:hidden bg-card/98 backdrop-blur-lg border-t border-border"
          >
            <div className="container mx-auto px-4 py-6 space-y-4">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className={`block py-2 text-base font-medium transition-colors ${
                      location.pathname === link.href
                        ? "text-primary"
                        : "text-foreground hover:text-primary"
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="pt-4 space-y-3"
                >
                {!user ? (
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full"
                    asChild
                  >
                    <Link to="/admin/login" className="flex items-center justify-center gap-2">
                      <LogIn className="h-5 w-5" />
                      Login
                    </Link>
                  </Button>
                ) : isAdmin ? (
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full"
                    asChild
                  >
                    <Link to="/admin" className="flex items-center justify-center gap-2">
                      <User className="h-5 w-5" />
                      Admin Dashboard
                    </Link>
                  </Button>
                ) : null}
                <Button
                  variant="whatsapp"
                  size="lg"
                  className="w-full"
                  asChild
                >
                  <a
                    href="https://api.whatsapp.com/send?phone=2347065732481"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Shop on WhatsApp
                  </a>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </header>
  );
}
